// Full-viewport lock screen overlay — requires PIN to unlock

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, AlertTriangle } from 'lucide-react';
import { PinInput } from '@/features/settings/PinInput';
import { verifyPin } from '@/features/settings/pin-api';
import { useLockStore } from '@/stores/lockStore';
import { useAuth } from '@/hooks/useAuth';
import { clearTokens } from '@/lib/auth';
import { cn } from '@/lib/utils';

interface LockScreenProps {
  /**
   * Full-viewport overlay (default) vs. content-area scoped. Content-scoped
   * is used inside FinancialLockGate so the sidebar/bottom-nav stay usable.
   */
  fullscreen?: boolean;
}

export function LockScreen({ fullscreen = true }: LockScreenProps = {}) {
  const { t } = useTranslation('settings');
  const { isLocked, unlock, incrementFailedAttempts, failedAttempts } = useLockStore();
  const { logout } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [shake, setShake] = useState(false);

  const handleVerify = useCallback(async (value: string) => {
    if (value.length !== 4) return;

    setIsVerifying(true);
    setError('');

    try {
      await verifyPin(value);
      unlock();
      setPin('');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';

      // Rate limited — force logout
      if (message.includes('rate') || message.includes('locked') || message.includes('Too many')) {
        clearTokens();
        logout();
        return;
      }

      const attempts = incrementFailedAttempts();
      setPin('');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setError(t('security.lockScreen.wrongPin', { attempts: String(attempts) }));
    } finally {
      setIsVerifying(false);
    }
  }, [unlock, incrementFailedAttempts, logout, t]);

  const handlePinChange = useCallback((value: string) => {
    setPin(value);
    setError('');
    if (value.length === 4) {
      handleVerify(value);
    }
  }, [handleVerify]);

  return (
    <AnimatePresence>
      {isLocked && (
        <motion.div
          key="lock-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            'flex items-center justify-center bg-background',
            fullscreen
              ? 'fixed inset-0 z-50'
              : 'min-h-[70vh] w-full py-12'
          )}
          data-testid="lock-screen"
        >
          <div className="flex flex-col items-center gap-6 px-6 w-full max-w-xs">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            >
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Lock className="size-8 text-muted-foreground" />
              </div>
            </motion.div>

            <div className="text-center space-y-1">
              <h1 className="text-xl font-semibold text-foreground">
                {t('security.lockScreen.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('security.lockScreen.subtitle')}
              </p>
            </div>

            <motion.div
              animate={shake ? { x: [0, -12, 12, -8, 8, -4, 4, 0] } : {}}
              transition={{ duration: 0.4 }}
            >
              <PinInput
                value={pin}
                onChange={handlePinChange}
                disabled={isVerifying}
                autoFocus
                aria-label={t('security.lockScreen.pinLabel')}
              />
            </motion.div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2 text-sm text-destructive"
                  role="alert"
                >
                  <AlertTriangle className="size-4 shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {failedAttempts > 0 && !error && (
              <p className="text-xs text-muted-foreground">
                {t('security.lockScreen.attemptsRemaining', {
                  count: 5 - failedAttempts,
                })}
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
